import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AppService } from '../app.service';
import { PrequalificationModel } from '../models/prequalification.model';
import { ApiRequestService } from '../shared/api-request.service';
import { RBACService } from '../shared/rbac.service';
import { UtilsService } from '../shared/utils.service';
import { CustomDataSource } from '../utils/custom-data-source';
import { PrequalificationService } from './prequalification.service';
import { PrequalificationsEditComponent } from './prequalifications-edit/prequalifications-edit.component';

@Component({
  selector: 'app-prequalifications',
  templateUrl: './prequalifications.component.html',
  styleUrls: ['./prequalifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PrequalificationsComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  // RBAC permission flags: resolved once in ngOnInit().
  canCreate: boolean = false;
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  dataSource = new PrequalificationsDataSource(this.app, this.api);

  // Build displayed_columns dynamically: NOT as a static array.
  displayed_columns: string[] = [];

  query: string;

  constructor(
    private app: AppService,
    private utils: UtilsService,
    private prequalService: PrequalificationService,
    private api: ApiRequestService,
    private dialog: MatDialog,
    public rbac: RBACService,
    private router: Router
  ) {}

  ngOnInit() {
    // 1. Resolve RBAC permission flags.
    this.canCreate = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_CONTRACTOR_PRE_QUALIFICATIONS,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );
    this.canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_CONTRACTOR_PRE_QUALIFICATIONS,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_CONTRACTOR_PRE_QUALIFICATIONS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_CONTRACTOR_PRE_QUALIFICATIONS,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    // 2. Page-level access: redirect if user has NO permissions at all.
    if (!this.canCreate && !this.canView && !this.canEdit && !this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access pre-qualification templates.'
      );
      this.router.navigate(['/dashboard']);
      return;
    }

    // 3. Build displayed_columns dynamically.
    this.displayed_columns = [];
    if (this.canDelete) {
      this.displayed_columns.push('select');
    }
    this.displayed_columns.push(
      'id',
      'name',
      'items',
      'default',
      'enable_notifications',
      'date_created',
      // 'date_created_UTC',
      'buttons'
    );

    // 4. Only load data if user has view permission.
    if (this.canView) {
      this.dataSource.getData(false);
    }
  }

  toggleDefault(id: number) {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit pre-qualification templates.'
      );
      return;
    }
    /*
    // Make an API request instead to check if there are any default pre-qualifications.
    let multipleDefault: boolean = false;
    this.dataSource.dataSubject.subscribe(res => {
      res.forEach((item: any) => {
        if (item.default === 1) multipleDefault = true;
      })
    });

    if (multipleDefault) {
      this.utils.showToast('You can only have one default Pre-qualification, please turn off others.');
      this.dataSource.getData(true);
    } else {
      this.prequalService.toggleDefault(id).subscribe(() => {
        this.utils.showToast('Toggled default Prequalification');
        this.dataSource.getData(true);
      });
    }
    //*/
    this.prequalService.toggleDefault(id).subscribe(() => {
      this.utils.showToast(
        'Default Pre-Qualification was successfully changed.'
      );
      this.dataSource.getData(true);
    });
  }

  toggleNotifications(id: number) {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit pre-qualification templates.'
      );
      return;
    }
    this.prequalService.toggleNotifications(id).then((response: any) => {
      this.utils.showToast('Toggled Notifications');
      // this.dataSource.getData(true);
    });
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_by = this.sort.active;
      this.dataSource.sort_order = this.sort.direction;

      // Sort UTC columns by their corresponding date columns.
      if (this.sort.active == 'date_created_UTC') {
        this.dataSource.sort_by = 'date_created';
      }

      this.dataSource.getData(false);
    });
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  public add() {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create pre-qualification templates.'
      );
      return;
    }
    this.openDialog(new PrequalificationModel());
  }

  public edit(prequalification: PrequalificationModel) {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit pre-qualification templates.'
      );
      return;
    }
    this.openDialog(
      Object.assign(new PrequalificationModel(), prequalification)
    );
  }

  private openDialog(prequalification: PrequalificationModel) {
    this.utils.showComponentDialog(
      PrequalificationsEditComponent,
      prequalification,
      {},
      (success) => {
        if (this.canView) {
          this.dataSource.getData(true);
        }
      }
    );
  }

  onRemoveSelected() {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete pre-qualification templates.'
      );
      return;
    }
    const prequalCount = this.dataSource.selection.selected.length;
    this.utils.showModal(
      'Remove Prequalification',
      `Are you sure you want to remove ${prequalCount} Prequalifications?`,
      () => {
        this.remove(this.dataSource.selection.selected);
      }
    );
  }

  onRemove(prequal: PrequalificationModel) {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete pre-qualification templates.'
      );
      return;
    }
    this.utils.showModal(
      'Remove Prequalification',
      `Are you sure you want to remove "${prequal.name}"?`,
      () => this.remove([prequal.id])
    );
  }

  private remove(ids: number[]) {
    this.prequalService.remove(ids).subscribe(() => {
      this.utils.showToast('Removed Prequalifications');
      this.dataSource.selection.clear();
      this.dataSource.getData(true);
    });
  }

  onCreateFrom(prequalification: PrequalificationModel) {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create pre-qualification templates.'
      );
      return;
    }
    const newPrequalification: PrequalificationModel = new PrequalificationModel();
    newPrequalification.createFrom(prequalification);
    this.openDialog(newPrequalification);
  }
}

export class PrequalificationsDataSource extends CustomDataSource {
  sort_by = 'name';
  sort_order = 'asc';
  pageSizeOptions = [5, 10, 15];

  getData(resetOffset: boolean = false) {
    this.getDataFromLaravelAPI('prequalifications?template=true', resetOffset);
  }
}
